import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Button } from '@/components/ui/button';
import { Copy, InfoIcon, CheckCircle, AlertTriangle, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

const WIDGET_VERSION = '1.0.3';
const SUPABASE_URL = 'https://lmniterbzhfihvxsumbl.supabase.co';
const DEMO_KEY = 'demo-installation-key-12345';

interface EmbedScriptProps {
  installationKey?: string;
  installationType?: 'standard' | 'webflow';
  demo?: boolean;
}

const EmbedScript = ({ installationKey, installationType = 'standard', demo = false }: EmbedScriptProps) => {
  const { toast } = useToast();
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [domain, setDomain] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("script");
  const [copied, setCopied] = useState<{[key: string]: boolean}>({});
  
  // Use demo key if demo is true, otherwise use provided key
  const effectiveKey = demo ? DEMO_KEY : installationKey;
  
  console.log("EmbedScript rendering with key:", effectiveKey, "type:", installationType, "demo:", demo);

  // Standard installation script
  const standardScriptCode = effectiveKey
    ? `<!-- Accesstopia Widget v${WIDGET_VERSION} -->

<!-- Add to your HTML head section -->
<link 
  rel="stylesheet" 
  href="${SUPABASE_URL}/functions/v1/widget/styles?v=${WIDGET_VERSION}"
/>

<!-- Add before </body> closing tag -->
<script>
  /* IMPORTANT: Only include ONE configuration block in your entire website */
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left', // Options: bottom-left, bottom-right, top-left, top-right
    voiceAssistantPosition: 'bottom-center', // Options: bottom-center, bottom-left, bottom-right
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true,
    debug: true // Set to true to see debug messages in console
  };
  
  // Wait for page to fully load
  document.addEventListener('DOMContentLoaded', function() {
    setTimeout(function() {
      (function loadAccesstopiaWidget() {
        try {
          if (window.AccesstopiaWidget) {
            console.warn('Accesstopia Widget already initialized');
            return;
          }
          
          console.log('Accesstopia: DOM fully loaded, initializing widget');
          
          var s = document.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = '${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey}&v=${WIDGET_VERSION}';
          s.setAttribute('data-accestopia', 'true');
          s.style.zIndex = '999999';
          
          s.onload = function() {
            console.log('Accesstopia: Widget loaded successfully');
            // Check if widget elements were created
            setTimeout(function() {
              if (!document.querySelector('.accesstopia-container')) {
                console.warn('Accesstopia: Widget elements not found, attempting reinit');
                var event = new Event('accestopia:reinit');
                document.dispatchEvent(event);
              }
            }, 1000);
          };
          
          s.onerror = function(e) { 
            console.error('Accesstopia: Failed to load widget:', e);
          };
          
          var x = document.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        } catch (err) {
          console.error('Accesstopia: Error during initialization:', err);
        }
      })();
    }, 500); // Small delay to ensure DOM is fully processed
  });
  
  // Fallback for browsers where DOMContentLoaded has already fired
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    console.log('Accesstopia: Document already loaded, initializing widget');
    setTimeout(function() {
      var event = new Event('DOMContentLoaded');
      document.dispatchEvent(event);
    }, 100);
  }
</script>`
    : `<!-- The widget installation script will be available after you get your unique installation key from your dashboard. -->`;

  // Webflow-specific installation script
  const webflowScriptCode = effectiveKey
    ? `/* 
============== WEBFLOW INSTALLATION INSTRUCTIONS ==============

1. Go to your Webflow site dashboard
2. Navigate to "Site Settings" → "Custom Code"
3. Copy the FIRST code block below into the "Head Code" section
4. Copy the SECOND code block below into the "Footer Code" section 
5. Save and publish your site

=========== HEAD CODE (Add to "Head Code" section) ===========
*/

<link 
  rel="stylesheet" 
  href="${SUPABASE_URL}/functions/v1/widget/styles?v=${WIDGET_VERSION}"
/>

/*
=========== FOOTER CODE (Add to "Footer Code" section) ===========
*/

<script>
  /* IMPORTANT: Only include ONE configuration block in your entire website */
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left',
    voiceAssistantPosition: 'bottom-center',
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true,
    zIndex: 999999, // Higher z-index to overcome Cloudflare
    debug: true // Enable for troubleshooting
  };

  // Specialized Webflow implementation
  (function() {
    // Try multiple initialization approaches
    var initAttempts = 0;
    var maxAttempts = 5;
    
    function initWidget() {
      try {
        if (window.AccesstopiaWidget) {
          console.warn('Accesstopia Widget already initialized');
          return;
        }
        
        console.log('Accesstopia: Initializing widget (attempt ' + (initAttempts + 1) + ')');
        
        // Create script element
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = '${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey}&v=${WIDGET_VERSION}';
        script.setAttribute('data-accestopia-webflow', 'true');
        
        // Add onload handler
        script.onload = function() {
          console.log('Accesstopia: Widget script loaded successfully');
          
          // Verify widget appearance
          setTimeout(function() {
            var widgetContainer = document.querySelector('.accesstopia-container');
            if (!widgetContainer) {
              console.warn('Accesstopia: Widget container not found, trying to force init');
              initAttempts++;
              if (initAttempts < maxAttempts) {
                console.log('Accesstopia: Retrying initialization...');
                setTimeout(initWidget, 1000 * initAttempts); // Increasing delay with each attempt
              }
            } else {
              console.log('Accesstopia: Widget successfully initialized');
              // Ensure widget has highest z-index
              widgetContainer.style.zIndex = '2147483647'; // Max z-index value
            }
          }, 1000);
        };
        
        // Add error handler
        script.onerror = function(e) {
          console.error('Accesstopia: Failed to load widget:', e);
          initAttempts++;
          if (initAttempts < maxAttempts) {
            setTimeout(initWidget, 1000 * initAttempts);
          }
        };
        
        // Add to DOM
        var firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(script, firstScript);
      } catch (err) {
        console.error('Accesstopia: Error during initialization:', err);
        initAttempts++;
        if (initAttempts < maxAttempts) {
          setTimeout(initWidget, 1000 * initAttempts);
        }
      }
    }
    
    // Initialize after a delay to ensure Webflow's own scripts have loaded
    setTimeout(initWidget, 800);
    
    // Add additional initialization trigger for delayed interactions
    document.addEventListener('click', function() {
      // If widget still not showing after user interaction, try again
      setTimeout(function() {
        if (!document.querySelector('.accesstopia-container') && initAttempts < maxAttempts) {
          console.log('Accesstopia: User interaction detected, attempting initialization');
          initWidget();
        }
      }, 500);
    }, { once: true });
  })();
</script>`
    : `<!-- The widget installation script will be available after you get your unique installation key from your dashboard. -->`;

  // Now we modify the bootstrap.ts function to be more resilient
  const updateBootstrapCode = () => {
    // This function would update the bootstrap code, but we're leaving it for now
    // as it requires edge function modifications
  };

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied({...copied, [type]: true});
    
    toast({
      title: "Copied!",
      description: `${type} code copied to clipboard.`,
    });
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopied(prev => ({...prev, [type]: false}));
    }, 2000);
  };
  
  const verifyInstallationKey = async () => {
    if (!effectiveKey || effectiveKey === DEMO_KEY) return;
    
    setVerificationStatus('loading');
    try {
      // Get auth token to make authenticated request
      const { data: sessionData } = await supabase.auth.getSession();
      const authHeader = sessionData?.session?.access_token 
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined;
      
      console.log('Verifying key with auth:', !!authHeader);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey}&verify=true`,
        { 
          signal: controller.signal,
          headers: {
            ...(authHeader || {}),
            'Content-Type': 'application/json'
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.error('Verification error:', response.status, response.statusText);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Verification response:', data);
      
      if (data.valid) {
        setVerificationStatus('valid');
        setDomain(data.domain);
        toast({
          title: "Valid Installation Key",
          description: `This key is active and ready to use on ${data.domain}.`,
        });
      } else {
        setVerificationStatus('invalid');
        toast({
          title: "Invalid Installation Key",
          description: "This key is either invalid or associated with an inactive subscription.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error verifying key:', error);
      setVerificationStatus('invalid');
      toast({
        title: "Verification Error",
        description: error.name === 'AbortError' 
          ? "Verification request timed out. Please try again." 
          : "Failed to verify installation key. Please check your subscription status and try again.",
        variant: "destructive",
      });
    }
  };
  
  useEffect(() => {
    setVerificationStatus('idle');
    setDomain(null);
  }, [effectiveKey]);
  
  // Display active script based on installationType prop
  const scriptToShow = installationType === 'webflow' ? webflowScriptCode : standardScriptCode;
  
  const renderCopyButton = (text: string, type: string, position: 'top' | 'bottom' = 'top') => (
    <Button
      size="sm"
      variant="outline"
      className={`flex items-center gap-1 ${position === 'top' ? 'absolute top-2 right-2' : 'ml-auto mt-2'}`}
      onClick={() => handleCopy(text, type)}
      aria-label={`Copy ${type} code`}
    >
      {copied[type] ? (
        <>
          <Check className="h-4 w-4" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          <span>Copy</span>
        </>
      )}
    </Button>
  );
  
  return (
    <div className="space-y-4">
      {/* Version badge */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Badge variant="outline">v{WIDGET_VERSION}</Badge>
        <span>Latest stable version</span>
      </div>

      {effectiveKey && (
        <>
          <div className="flex justify-between items-center">
            <Alert className="flex-grow">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Your installation key is unique and tied to your subscription. Do not share it publicly.
              </AlertDescription>
            </Alert>
            
            {!demo && (
              <Button 
                variant="outline" 
                className="ml-2 whitespace-nowrap" 
                onClick={verifyInstallationKey}
                disabled={verificationStatus === 'loading'}
              >
                {verificationStatus === 'loading' ? 'Verifying...' : 'Verify Key'}
              </Button>
            )}
          </div>
          
          {verificationStatus !== 'idle' && !demo && (
            <div className={`p-3 rounded-md ${
              verificationStatus === 'valid' 
                ? 'bg-green-50 dark:bg-green-900/20' 
                : verificationStatus === 'invalid' 
                  ? 'bg-red-50 dark:bg-red-900/20' 
                  : 'bg-muted'
            }`}>
              {verificationStatus === 'valid' && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-300">Valid Installation Key</p>
                    {domain && (
                      <p className="text-sm text-green-700 dark:text-green-400">
                        Registered for: <Badge variant="outline">{domain}</Badge>
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {verificationStatus === 'invalid' && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <div>
                    <p className="font-medium text-red-800 dark:text-red-300">Invalid Installation Key</p>
                    <p className="text-sm text-red-700 dark:text-red-400">
                      This key is either invalid or the associated subscription is inactive.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      <Tabs defaultValue="script" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="script" className="flex-1">Script Tag</TabsTrigger>
          <TabsTrigger value="cdn" className="flex-1">CDN</TabsTrigger>
          {installationType === 'webflow' && (
            <TabsTrigger value="webflow" className="flex-1">Webflow</TabsTrigger>
          )}
          <TabsTrigger value="config" className="flex-1">Configuration</TabsTrigger>
        </TabsList>
        
        <TabsContent value="script" className="mt-2">
          <div className="relative">
            <Alert className="mb-4">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Add the stylesheet to the <code>&lt;head&gt;</code> section and the script right before the closing <code>&lt;/body&gt;</code> tag.
                <strong className="block mt-1">NOTE: Only include ONE configuration block in your entire website!</strong>
              </AlertDescription>
            </Alert>
            <div className="relative">
              <SyntaxHighlighter
                language="html"
                style={tomorrow}
                customStyle={{
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  backgroundColor: 'hsl(240, 3.7%, 15.9%)',
                  fontSize: '0.9rem',
                }}
              >
                {standardScriptCode}
              </SyntaxHighlighter>
              {effectiveKey && renderCopyButton(standardScriptCode, 'Script', 'top')}
            </div>
            {effectiveKey && renderCopyButton(standardScriptCode, 'Script', 'bottom')}
          </div>
        </TabsContent>
        
        {installationType === 'webflow' && (
          <TabsContent value="webflow" className="mt-2">
            <div className="relative">
              <Alert className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <InfoIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  <strong>Webflow-Specific Installation:</strong> Please follow the instructions in the comments.
                  This version is optimized to work with Webflow's security settings and Cloudflare.
                </AlertDescription>
              </Alert>
              <div className="relative">
                <SyntaxHighlighter
                  language="html"
                  style={tomorrow}
                  customStyle={{
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    backgroundColor: 'hsl(240, 3.7%, 15.9%)',
                    fontSize: '0.9rem',
                  }}
                >
                  {webflowScriptCode}
                </SyntaxHighlighter>
                {effectiveKey && renderCopyButton(webflowScriptCode, 'Webflow Script', 'top')}
              </div>
              {effectiveKey && renderCopyButton(webflowScriptCode, 'Webflow Script', 'bottom')}
            </div>
          </TabsContent>
        )}
        
        <TabsContent value="cdn" className="mt-2">
          <Alert className="mb-4">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              Alternative CDN installation method with separate stylesheet and script tags.
              <strong className="block mt-1">IMPORTANT: This method must be implemented exactly as shown below.</strong>
            </AlertDescription>
          </Alert>
          <div className="relative">
            <SyntaxHighlighter
              language="html"
              style={tomorrow}
              customStyle={{
                borderRadius: '0.5rem',
                padding: '1rem',
                backgroundColor: 'hsl(240, 3.7%, 15.9%)',
                fontSize: '0.9rem',
              }}
            >
              {`<!-- Add to your HTML <head> -->
<link 
  rel="stylesheet" 
  href="${SUPABASE_URL}/functions/v1/widget/styles?v=${WIDGET_VERSION}"
/>

<!-- Add before </body> -->
<!-- IMPORTANT: Only include this configuration block ONCE in your entire website -->
<script>
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left',
    voiceAssistantPosition: 'bottom-center',
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true,
    language: 'en-US',
    debug: true // Set to true for troubleshooting
  };
</script>
<script 
  src="${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey || 'YOUR_INSTALLATION_KEY'}&v=${WIDGET_VERSION}"
  async
></script>`}
            </SyntaxHighlighter>
            {effectiveKey && renderCopyButton(`<!-- Add to your HTML <head> -->
<link 
  rel="stylesheet" 
  href="${SUPABASE_URL}/functions/v1/widget/styles?v=${WIDGET_VERSION}"
/>

<!-- Add before </body> -->
<!-- IMPORTANT: Only include this configuration block ONCE in your entire website -->
<script>
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left',
    voiceAssistantPosition: 'bottom-center',
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true,
    language: 'en-US',
    debug: true // Set to true for troubleshooting
  };
</script>
<script 
  src="${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey}&v=${WIDGET_VERSION}"
  async
></script>`, 'CDN', 'top')}
          </div>
          {effectiveKey && renderCopyButton(`<!-- Add to your HTML <head> -->
<link 
  rel="stylesheet" 
  href="${SUPABASE_URL}/functions/v1/widget/styles?v=${WIDGET_VERSION}"
/>

<!-- Add before </body> -->
<!-- IMPORTANT: Only include this configuration block ONCE in your entire website -->
<script>
  window.ACCESSTOPIA_CONFIG = {
    buttonPosition: 'bottom-left',
    voiceAssistantPosition: 'bottom-center',
    defaultDarkMode: false,
    defaultFontSize: 100,
    primaryColor: '#0066cc',
    borderRadius: '0.5rem',
    enableVoiceAssistant: true,
    language: 'en-US',
    debug: true // Set to true for troubleshooting
  };
</script>
<script 
  src="${SUPABASE_URL}/functions/v1/widget?key=${effectiveKey}&v=${WIDGET_VERSION}"
  async
></script>`, 'CDN', 'bottom')}
        </TabsContent>
        
        <TabsContent value="config" className="mt-2">
          <Alert className="mb-4">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              All available configuration options for the Accesstopia widget.
            </AlertDescription>
          </Alert>
          <div className="relative">
            <SyntaxHighlighter
              language="javascript"
              style={tomorrow}
              customStyle={{
                borderRadius: '0.5rem',
                padding: '1rem',
                backgroundColor: 'hsl(240, 3.7%, 15.9%)',
                fontSize: '0.9rem',
              }}
            >
              {`window.ACCESSTOPIA_CONFIG = {
  // Widget button position ('bottom-left', 'bottom-right', 'top-left', 'top-right')
  buttonPosition: 'bottom-left',
  
  // Voice assistant position ('bottom-center', 'bottom-left', 'bottom-right') 
  voiceAssistantPosition: 'bottom-center', 
  
  // Default dark mode state (boolean)
  defaultDarkMode: false, 
  
  // Default font size percentage (number: 80-200)
  defaultFontSize: 100, 
  
  // Primary color for UI elements (hex color)
  primaryColor: '#0066cc', 
  
  // Border radius for UI elements (CSS value)
  borderRadius: '0.5rem', 
  
  // Enable or disable voice assistant (boolean)
  enableVoiceAssistant: true, 
  
  // Voice recognition language (BCP 47 language tag)
  language: 'en-US', 
  
  // Show debug messages in console (boolean)
  debug: false,
  
  // Custom callback when accessibility settings change
  onStateChange: function(state) {
    console.log('Accessibility state changed:', state);
  }
};`}
            </SyntaxHighlighter>
            {renderCopyButton(`window.ACCESSTOPIA_CONFIG = {
  // Widget button position ('bottom-left', 'bottom-right', 'top-left', 'top-right')
  buttonPosition: 'bottom-left',
  
  // Voice assistant position ('bottom-center', 'bottom-left', 'bottom-right') 
  voiceAssistantPosition: 'bottom-center', 
  
  // Default dark mode state (boolean)
  defaultDarkMode: false, 
  
  // Default font size percentage (number: 80-200)
  defaultFontSize: 100, 
  
  // Primary color for UI elements (hex color)
  primaryColor: '#0066cc', 
  
  // Border radius for UI elements (CSS value)
  borderRadius: '0.5rem', 
  
  // Enable or disable voice assistant (boolean)
  enableVoiceAssistant: true, 
  
  // Voice recognition language (BCP 47 language tag)
  language: 'en-US', 
  
  // Show debug messages in console (boolean)
  debug: false,
  
  // Custom callback when accessibility settings change
  onStateChange: function(state) {
    console.log('Accessibility state changed:', state);
  }
};`, 'Config', 'top')}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* ... keep existing code (features, troubleshooting sections, etc) */}
      
    </div>
  );
};

export default EmbedScript;
